import debounce from 'lodash/debounce';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import {ProductPriceData} from '@/components/containers/pdp/utils/product-price-data';
import {ConsumerProductPriceData} from '@/components/containers/pdp/utils/consumer-product-price-data';
import {isBuildToOrderByCode} from '@/components/containers/pdp/utils/retailers-helper';
import {mapGetters, mapMutations, mapState, mapActions} from 'vuex';

const ProductDetails = {
  props: {
    product: {
      type: Object,
      required: true,
    },
    options: {
      type: Object,
      required: true,
    },
    copyPositioningStatement: {
      type: String,
      default: '',
      required: false,
    },
  },

  watch: {
    variantCode(value) {
      if (!this.showLocallyWidget && !this.options.isLocallyActive) {
        this.$emit('loadRpaResults', value);
      }
    },
  },

  computed: {
    ...mapGetters('backend', ['b2b', 'consumerFriendlyMode']),
    ...mapState('productBundle', ['buyingZoneQuantity']),

    variant() {
      if (!this.variantCode) {
        return {};
      }

      const variant = this.product.variants.find((variant) => variant.code === this.variantCode);
      return !variant ? {} : variant;
    },

    isPurchasable() {
      return (
        get(this.variant, 'isPurchasable', false) && !isBuildToOrderByCode(get(this.stockData, 'globalStockStatus'))
      );
    },

    isBike() {
      return get(this.product, 'productType') === 'bike';
    },

    projectOneUrl() {
      return get(this.product, 'projectOneUrl', '');
    },

    hasImages() {
      return this.product.images && Object.keys(this.product.images).length > 0;
    },

    hasPrice() {
      if (!this.pricingData || !this.pricingData.retailerPrice) {
        return false;
      }

      const price = get(
        this.pricingData,
        'retailerPrice.price.formatted',
        get(this.pricingData, 'retailerPrice.price.low.formatted')
      );
      return !isEmpty(price);
    },

    consumerPrice() {
      let pricingData = get(this.pricingData, 'consumerPrice') || get(this.product, 'consumerPrice');
      if (!pricingData && this.consumerFriendlyMode) {
        return new ConsumerProductPriceData(this.populatePriceData(pricingData, []), {
          b2b: this.b2b,
        });
      }
      if (!pricingData || !Object.keys(pricingData).length) {
        return this.retailerPrice;
      }

      return new ConsumerProductPriceData(this.populatePriceData(pricingData, ['apple'], 'retailerPrice'), {
        b2b: this.b2b,
      });
    },

    retailerPrice() {
      let pricingData = get(this.pricingData, 'retailerPrice', {});
      if (!pricingData || !Object.keys(pricingData).length) {
        pricingData = get(this.product, 'retailerPrice', {});
      }

      return new ProductPriceData(
        this.populatePriceData(pricingData, ['advertised', 'saleAdvertised'], 'consumerPrice'),
        {b2b: this.b2b}
      );
    },
  },

  methods: {
    populatePriceData(target, keys, from) {
      if (!target) {
        target = {};
      }

      const priceData = !this.pricingData || !Object.keys(this.pricingData).length ? this.product : this.pricingData;
      keys.forEach((key) => {
        const itemData = get(priceData, `${from}.${key}`);
        if (itemData) {
          target[key] = itemData;
        }
      });

      return target;
    },

    updateShowLocallyWidget(state) {
      this.showLocallyWidget = state;
      if (state) {
        this.$emit('loadRpaResults', this.variantCode);
      }
    },

    loadPricing: debounce(function(productCode) {
      if (!productCode) {
        this.pricingData = {};
      } else {
        const pricing = this.product.variants.find((variant) => variant.code === productCode);
        if (pricing) {
          this.pricingData = {
            retailerPrice: get(pricing, 'retailerPrice'),
            consumerPrice: get(pricing, 'consumerPrice'),
          };
        }
      }
    }, 10),

    getBuyingZoneBundleProductData(variantCode) {
      let hierarchyValue = {};
      this.categorizationMetadata = [];
      this.clearMainBundleProducts();
      this.setIsVariantCode(variantCode);
      this.setBundleMainProductName(this.product.name);
      this.setBundleMainProductQuantity(this.buyingZoneQuantity);

      const variantImage = document.querySelector('#heroCarousel .swiper-slide-active img');
      if (variantImage) {
        this.setBundleMainProductUrl(variantImage.src);
      }
      hierarchyValue = this.product.categorizationHierarchyValues.find(
        (element) => JSON.stringify(element).indexOf(variantCode) !== -1
      );

      this.product.categorizationHierarchyMetadata.forEach((metaData) => {
        if (metaData && !metaData.code.includes('color')) {
          this.getVariantDisplayName(metaData, hierarchyValue, variantCode);
        }
      });
    },

    getVariantDisplayName(metaData, hierarchyValue, variantCode) {
      if (hierarchyValue) {
        if (Array.isArray(hierarchyValue)) {
          hierarchyValue.forEach((element) => {
            this.getVariantDisplayName(metaData, element, variantCode);
          });
        } else {
          if (hierarchyValue.options.productCode === variantCode && hierarchyValue.code === metaData.code) {
            this.categorizationMetadata.push({
              key: metaData.options.displayName,
              value: hierarchyValue.options.displayName,
            });
          } else if (hierarchyValue.children.length > 0 && hierarchyValue.code === metaData.code) {
            this.categorizationMetadata.push({
              key: metaData.options.displayName,
              value: hierarchyValue.options.displayName,
            });
          } else if (hierarchyValue.children.length > 0) {
            this.getVariantDisplayName(metaData, hierarchyValue.children, variantCode);
          }
        }
      }
      this.setBundleMainProductMetaData(this.categorizationMetadata);
    },

    ...mapMutations('productBundle', [
      'setIsVariantCode',
      'setBundleMainProductMetaData',
      'setBundleMainProductName',
      'setBundleMainProductUrl',
      'setBundleMainProductQuantity',
    ]),
    ...mapActions('productBundle', ['clearMainBundleProducts']),
  },

  data() {
    return {
      selectedColor: '',
      variantCode: '',
      pricingData: null,
      showLocallyWidget: this.options.isLocallyActive,
      selectedOption: null,
      categorizationMetadata: [],
    };
  },
};

export {ProductDetails};
